import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { G_DARK, Field, PasswordInput, PasswordStrength, passwordPolicyChecks, Btn, globalStyles } from "../components/Shared";

// The page knows a token, not the account (the token's subject is an id), so it
// cannot judge "does not contain the e-mail address". Given no identity, boku-ui's
// PasswordRequirements would tick that row as met and call it a *username*;
// an account here is always an e-mail. So the row is left out of the ticks
// and said in words, and the backend checks it on save.
const identityKnown = c => !c.label.startsWith("Does not contain");

function ResetRequirements({ password }) {
  if (!password) return null;
  const checks = passwordPolicyChecks(password).filter(identityKnown);
  return (
    <ul style={{ margin: "8px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 3 }}>
      {checks.map(c => (
        <li key={c.label} style={{ fontSize: 11, color: c.ok ? "#15803d" : "#6b7280", display: "flex", gap: 6 }}>
          <span aria-hidden="true">{c.ok ? "✓" : "○"}</span>
          <span>{c.label}{c.ok ? " — met" : ""}</span>
        </li>
      ))}
      <li style={{ fontSize: 11, color: "#6b7280", display: "flex", gap: 6 }}>
        <span aria-hidden="true">○</span>
        <span>Does not contain the e-mail address — checked when you save</span>
      </li>
    </ul>
  );
}

const acceptable = password => passwordPolicyChecks(password).filter(identityKnown).every(c => c.ok);

export default function ResetPasswordPage() {
  const [searchParams]        = useSearchParams();
  const token                 = searchParams.get("token") || "";
  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const [message, setMessage] = useState(null);

  const handleSubmit = async e => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      const res = await api.post("/auth/reset-password", { token, new_password: password });
      setMessage(res.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: 24 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap" rel="stylesheet" />
      <style>{globalStyles}</style>

      <div style={{ maxWidth: 400, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <img src="/csm_binf.png" alt="BOKU Core Facility Bioinformatics" style={{ height: 60, borderRadius: 12, marginBottom: 16 }} />
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1a1a1a" }}>Reset Password</h1>
          <p style={{ margin: "6px 0 0", fontSize: 14, color: "#6b7280" }}>BOKU Core Facility Bioinformatics</p>
        </div>

        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          <div style={{ padding: 28 }}>
            {!token ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <div role="alert" style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
                  This link is missing its reset token. Please use the link from the email exactly as sent.
                </div>
                <Link to="/forgot" style={{ fontSize: 13, color: G_DARK, textDecoration: "none", fontWeight: 500, textAlign: "center" }}>
                  Request a new reset link
                </Link>
              </div>
            ) : message ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(0,150,64,0.08)", border: "1px solid rgba(0,150,64,0.25)", color: G_DARK, fontSize: 13 }}>
                  {message}
                </div>
                <Link to="/login" style={{ fontSize: 13, color: G_DARK, textDecoration: "none", fontWeight: 500, textAlign: "center" }}>
                  Go to sign in
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <Field label="New password" required>
                  <PasswordInput
                    autoFocus
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                  />
                  <PasswordStrength password={password} />
                  <ResetRequirements password={password} />
                </Field>
                <Field label="Confirm new password" required>
                  <PasswordInput
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                  />
                </Field>

                {error && (
                  <div role="alert" style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
                    {error}
                    {error.toLowerCase().includes("invalid") || error.toLowerCase().includes("expired") ? (
                      <div style={{ marginTop: 8 }}>
                        <Link to="/forgot" style={{ color: "#b91c1c", fontWeight: 600 }}>Request a new reset link</Link>
                      </div>
                    ) : null}
                  </div>
                )}

                <Btn type="submit" disabled={busy || !confirm || !acceptable(password)} style={{ width: "100%", justifyContent: "center", padding: "11px 0" }}>
                  {busy ? "Saving…" : "Set new password"}
                </Btn>

                <Link to="/login" style={{ fontSize: 13, color: "#6b7280", textDecoration: "none", textAlign: "center" }}>
                  ← Back to sign in
                </Link>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
